import { SURF_SPOTS } from './surf-forecast.mjs'

export const SURF_GUIDE_SEO = {
  titleFormat: 'complete',
  metaTitle: 'Selong Belanak Surf Guide: Beaches, Seasons & Lessons',
  metaDesc: 'Plan surfing in Selong Belanak, Lombok: compare beginner beach breaks with Mawi and Serangan reef surf, check seasonal guidance and find lessons and forecasts.',
}
export function surfGuideSeo(seo = {}) {
  return { ...seo, ...SURF_GUIDE_SEO, shareTitle: SURF_GUIDE_SEO.metaTitle, shareDesc: SURF_GUIDE_SEO.metaDesc }
}
export const SURF_ARTICLE_SLUGS = [
  'beginner-surfing-selong-belanak',
  'selong-belanak-serangan-mawi-beach-guide',
  'best-time-to-visit-selong-belanak',
]
export const SURF_PLANNING_LINKS = [
  { id: 'surf-guide', href: '/beaches-surfing', label: 'Selong Belanak surf & beach guide', text: 'Choose a beach for your ability and time of year.' },
  { id: 'surf-forecast', href: '/surf-forecast', label: 'Waves, wind & estimated tide forecast', text: 'Seven days of regional forecasts in WITA.' },
  { id: 'surf-schools', href: '/surf', label: 'Surf schools & lessons', text: 'Compare local schools and check lesson details.' },
]

export const SURF_SEASON_GUIDANCE = 'South Lombok’s dry-season trade winds generally come from the east to southeast, blowing toward the west to northwest. This pattern often favours Mawi, especially with easterly winds. In the wet season, winds more often come from the west to northwest; northwesterly winds can be offshore at Serangan. Serangan can also work at low tide during the dry season when the wind is nearly still, often early in the morning. Actual wind, swell and tide decide the day’s options.'

// Editorial context combines the destination guides, local tide and calm-morning
// observations, and published wind exposure. It is not a live surf assessment.
export const SURF_BEACH_CONTEXT = {
  'selong-belanak-beach': {
    summary: 'Selong Belanak Beach is the area’s main sandy-bottom beginner surf beach, with schools and board rentals along the bay. Lessons run on suitable days across the year; swell, wind and tide still change the conditions.',
    heading: 'Planning a first surf lesson',
    detail: 'A small offshore forecast does not tell you whether the whitewater is suitable for a first lesson. Ask an instructor to choose the time and part of the beach for your swimming confidence and experience.',
    link: { href: '/blog/beginner-surfing-selong-belanak', label: 'Read the beginner surfing guide' },
  },
  'mawi-beach': {
    summary: 'Mawi Beach is a reef-surf option for experienced surfers near Selong Belanak and is not suitable for beginners. Southwest groundswell and easterly offshore wind are preferred, often aligning with the dry season. Light southeast wind can sometimes stay clean because of the valley; stronger or more southerly wind can become cross-shore.',
    heading: 'Mawi swell, wind and tide',
    detail: 'Mid tide is preferred, but higher tide can also work, especially for the right. The right works on smaller to medium swells; the left becomes more prominent and demanding as the swell grows. Low tide exposes shallow, sharp reef. No minimum offshore swell height or period has been locally confirmed. Offshore height cannot describe the breaking wave or reef depth; check conditions, currents, entry and exit with an experienced local guide.',
    link: { href: '/blog/selong-belanak-serangan-mawi-beach-guide', label: 'Compare Mawi with Selong Belanak and Serangan' },
  },
  'serangan-beach': {
    summary: 'This Serangan Beach is on Lombok’s south coast near Selong Belanak, Indonesia, not on Bali. Its right-hand reef break suits confident intermediate and advanced surfers and is not suitable for beginners. Winds from the northwest to north-northwest can be offshore here, blowing toward the southeast to south-southeast. These favourable winds generally align with the wet-season pattern.',
    heading: 'Serangan wind and low tide',
    detail: 'Local guidance favours low tide for the Serangan wave. During the dry season, low tide can still offer good waves when the wind is nearly still, often early in the morning. Check the actual wind rather than relying on the time of day. Swell and reef exposure still matter; confirm conditions locally. The tide estimates here use a regional sea-level model and need local confirmation.',
    link: { href: '/blog/selong-belanak-serangan-mawi-beach-guide', label: 'Read the South Lombok beach comparison' },
  },
}

export function surfForecastSeo(spot, overview = false) {
  return {
    titleFormat: 'complete',
    metaTitle: overview ? 'Selong Belanak Surf Forecast | Waves, Wind & Tide' : `${spot.name} Surf Forecast | ${spot.slug === 'selong-belanak-beach' ? 'Lombok' : 'South Lombok'}`,
    metaDesc: overview
      ? 'Seven-day offshore waves, swell, wind and estimated tides for Selong Belanak, Mawi and Serangan on Lombok’s south coast, Indonesia. Hourly times in WITA.'
      : spot.slug === 'serangan-beach'
        ? 'Seven-day Serangan Beach forecast near Selong Belanak, South Lombok, Indonesia: offshore swell, wind and estimated tides in WITA. This is Lombok, not Bali.'
        : `Plan surfing at ${spot.name}, South Lombok, Indonesia: seven-day offshore swell and wind, estimated tides in WITA and local beach guidance.`,
  }
}
export function surfForecastHeading(spot, overview = false) {
  return overview ? 'Surf forecast around Selong Belanak' : `${spot.name} surf forecast`
}
export function surfForecastBreadcrumb(spot, overview, siteUrl) {
  const items = [
    { name: 'Home', path: '' },
    { name: 'Beaches & surfing', path: '/beaches-surfing' },
    { name: 'Surf forecast', path: '/surf-forecast' },
  ]
  if (!overview) items.push({ name: spot.name, path: `/surf-forecast/${spot.slug}` })
  return {
    '@context': 'https://schema.org', '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({ '@type': 'ListItem', position: index + 1, name: item.name, item: `${siteUrl}${item.path}` })),
  }
}
export function surfForecastPageSchema(spot, overview, siteUrl) {
  const seo = surfForecastSeo(spot, overview)
  return {
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: surfForecastHeading(spot, overview), description: seo.metaDesc,
    url: `${siteUrl}/surf-forecast${overview ? '' : `/${spot.slug}`}`, inLanguage: 'en',
    about: (overview ? SURF_SPOTS : [spot]).map(beach => ({
      '@type': 'Place', name: beach.name,
      geo: { '@type': 'GeoCoordinates', latitude: beach.latitude, longitude: beach.longitude },
      containedInPlace: { '@type': 'Place', name: 'South Lombok, West Nusa Tenggara, Indonesia' },
    })),
  }
}
